import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ConfigProvider, App as AntApp } from "antd";
import "./App.css";

// Import components directly to avoid potential import issues
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import Categories from "./components/Categories";
import ProductSection from "./components/ProductSection";
import Newsletter from "./components/Newsletter";
import Footer from "./components/Footer";
import { Home, NotFound } from "./pages";
import ProductDetail from "./pages/ProductDetail";
import Shop from "./pages/Shop";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import MyAccount from "./pages/MyAccount";
import Addresses from "./pages/Addresses";
import PersonalInfo from "./pages/PersonalInfo";
import Orders from "./pages/Orders";
import OrderDetails from "./pages/OrderDetails";
import Wishlist from "./pages/Wishlist";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import ReturnAndRefund from "./pages/ReturnAndRefund";
import ForgotPassword from "./pages/ForgotPassword";
import ScrollToTop from "./utils/ScrollToTop";
import AOS from "aos";
import { useDispatch, useSelector } from "react-redux";
const App = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.isLoggedIn);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    AOS.init({
      once: true,
      duration: 650,
    });

    // Helper function to get cookie by name
    const getCookie = (name) => {
      const nameEQ = name + "=";
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i].trim();
        if (cookie.indexOf(nameEQ) === 0) {
          return decodeURIComponent(cookie.substring(nameEQ.length));
        }
      }
      return null;
    };

    const userData = getCookie("userData");

    if (userData) {
      try {
        const decodedData = JSON.parse(atob(userData));
        dispatch({ type: "logIn", data: decodedData });
      } catch (e) {
        console.error("Failed to parse userData cookie", e);
      }
    }

    // Mark initialization as complete
    setIsInitialized(true);

    return () => {};
  }, [dispatch]);

  // Wait for initialization before rendering routes
  if (!isInitialized) {
    return null;
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#000000",
          borderRadius: 6,
          fontFamily: "'Outfit', sans-serif",
        },
      }}
    >
      <AntApp>
        <ScrollToTop />
        <Navbar />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/clothes" element={<Shop />} />
          <Route path="/clothes/:cat" element={<Shop />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:slug" element={<ProductDetail />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditions />}
          />
          <Route path="/return-refund" element={<ReturnAndRefund />} />

          {/* Protected Routes - Only accessible when logged in */}
          {isLoggedIn ? (
            <>
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/my-account" element={<MyAccount />} />
              <Route path="/personal-info" element={<PersonalInfo />} />
              <Route path="/addresses" element={<Addresses />} />
              <Route path="/wishlist" element={<Wishlist />} />
              <Route
                path="/order-confirmation/:id"
                element={<OrderConfirmation />}
              />
              <Route path="/orders" element={<Orders />} />
              <Route
                path="/order/:orderNumber/:id"
                element={<OrderDetails />}
              />
            </>
          ) : (
            <>
              <Route path="/cart" element={<Navigate to="/" />} />
              <Route path="/checkout" element={<Navigate to="/" />} />
              <Route path="/my-account" element={<Navigate to="/" />} />
              <Route path="/personal-info" element={<Navigate to="/" />} />
              <Route path="/addresses" element={<Navigate to="/" />} />
              <Route path="/wishlist" element={<Navigate to="/" />} />
              <Route path="/order-confirmation" element={<Navigate to="/" />} />
              <Route path="/orders" element={<Navigate to="/" />} />
              <Route path="/order/:id" element={<Navigate to="/" />} />
            </>
          )}

          {/* Auth Routes - Only accessible when not logged in */}
          {!isLoggedIn ? (
            <>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
            </>
          ) : (
            <>
              <Route path="/login" element={<Navigate to="/" />} />
              <Route path="/signup" element={<Navigate to="/" />} />
              <Route path="/forgot-password" element={<Navigate to="/" />} />
            </>
          )}

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
